<script setup lang="ts">
  import { onBeforeMount, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import { ComboSelect } from '@ui/components/combo-select';

  interface Props {
    queryName?: string;
  }

  interface ClassificationOption {
    label: string;
    value: string;
  }

  withDefaults(defineProps<Props>(), {
    queryName: 'classifications_any',
  });

  const emit = defineEmits<{
    (e: 'update', value: string[]): void;
  }>();

  const route = useRoute();
  const selectedClassification = ref<ClassificationOption[]>([]);

  const classificationOptions: ClassificationOption[] = [
    { label: 'Account inquiry', value: 'account_inquiry' },
    { label: 'Cancellation request', value: 'cancellation_request' },
    { label: 'Complaint', value: 'complaint' },
    { label: 'Contact inquiry', value: 'contact_inquiry' },
    { label: 'Dispute', value: 'dispute' },
    { label: 'Feedback', value: 'feedback' },
    { label: 'Invoice inquiry', value: 'invoice_inquiry' },
    { label: 'Other', value: 'other' },
    { label: 'Payment inquiry', value: 'payment_inquiry' },
    { label: 'Payment promise', value: 'payment_promise' },
    { label: 'Refund request', value: 'refund_request' },
    { label: 'Support request', value: 'support_request' },
    { label: 'Wrong contact', value: 'wrong_contact' },
  ];

  onBeforeMount(() => {
    if (route.query?.classifications_any) {
      const classificationQuery = route.query.classifications_any;
      const classificationArray = Array.isArray(classificationQuery)
        ? classificationQuery
        : [classificationQuery];

      selectedClassification.value = classificationArray
        .map((val) => {
          return classificationOptions?.find((option) => option.value === val);
        })
        .filter((option): option is ClassificationOption => option !== undefined);
    }
  });

  defineExpose({
    selectedClassification,
  });
</script>

<template>
  <ComboSelect
    v-model="selectedClassification"
    :options="classificationOptions"
    searchable
    variant="filter"
    placeholder="Categories"
    :multiple="true"
    search-placeholder="Filter by category..."
    option-identifier="label"
    @update:model-value="
      (classification) =>
        emit(
          'update',
          Array.isArray(classification)
            ? classification?.map((c) => c.value)
            : classification
              ? [classification.value]
              : [],
        )
    "
  >
    <template #filter-label>
      <i class="fa-regular fa-tags text-xs" />
      <span> Categories </span>
    </template>
  </ComboSelect>
</template>
